import { Link } from "react-router-dom";
import { ChevronLeft, MessageSquareText } from "lucide-react";

// Plain, human-readable Terms of Service. Required alongside a Privacy Policy
// for Play Console + AdSense approval — served at /terms on notelosh.com.
export default function Terms() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F3F4F6]">
      <header className="sticky top-0 z-10 bg-[#0A0A0A]/90 backdrop-blur border-b border-[#27272A]">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#9CA3AF] hover:text-[#F3F4F6] transition-colors">
            <ChevronLeft className="h-4 w-4" /> Back
          </Link>
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-lg bg-[#0A0A0A] border border-[#27272A] flex items-center justify-center overflow-hidden">
              <img src="/icons/logo-nl.png" alt="" className="h-5 w-5 object-contain" />
            </div>
            <span className="font-heading text-base font-bold tracking-tight">NoteLosh</span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-2">Terms of Service</h1>
        <p className="text-sm text-[#6B7280] mb-10">Last updated: 30 October 2026</p>

        <div className="space-y-8 text-[#D1D5DB] leading-relaxed">
          <section>
            <p>By using NoteLosh (the "Service"), you agree to these terms. If you don't agree, don't use the Service. We may update these terms; material changes are announced in-app or by email at least 14 days before they take effect.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#F3F4F6] mb-3">1. What NoteLosh is</h2>
            <p>NoteLosh is a personal notes & links workspace with an AI search and a marketplace where users can publish and sell their own note packs. Available as a website (notelosh.com), a Progressive Web App, and a native Android app.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#F3F4F6] mb-3">2. Your account</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>You must be at least 13 years old to create an account.</li>
              <li>You are responsible for anything done through your account. Keep your password safe.</li>
              <li>One account per person. Automated / bulk-created accounts may be terminated.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#F3F4F6] mb-3">3. Content you post</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>You keep ownership of everything you save or publish. You grant NoteLosh a limited licence to store, display, and process it so we can run the Service.</li>
              <li>You must have the right to post whatever you upload — no infringing, illegal, harmful, or misleading content.</li>
              <li>We may remove content that violates these terms or the law, and terminate repeat offenders.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#F3F4F6] mb-3">4. Marketplace</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Sellers set their own prices. NoteLosh takes a <span className="text-[#F3F4F6]">20% platform commission</span> on each sale (subject to change with 30 days' notice).</li>
              <li>Web purchases, wallet top-ups and seller payouts are processed by Stripe. Note purchases made inside the Android app are processed by Google Play Billing under Google Play's policies.</li>
              <li>Wallet top-ups can only be made on the website (notelosh.com). The Android app links to the website for top-ups and does not process them itself.</li>
              <li>All sales are final unless the note pack is materially misrepresented — refund requests go to <a href="mailto:support@notelosh.com" className="text-[#3B82F6] hover:underline">support@notelosh.com</a>.</li>
              <li>Sellers are responsible for their own taxes on marketplace earnings.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#F3F4F6] mb-3">5. Prohibited use</h2>
            <p>You may not:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Use the Service for illegal activity, harassment, spam, malware, phishing, or CSAM.</li>
              <li>Scrape, crawl, or hammer our API beyond reasonable personal use.</li>
              <li>Reverse-engineer or attempt to breach our security.</li>
              <li>Impersonate others or misrepresent your affiliation.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#F3F4F6] mb-3">6. Termination</h2>
            <p>You can delete your account any time from Settings. We can suspend or terminate accounts that violate these terms. On termination, we delete your personal data within 30 days (see Privacy Policy).</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#F3F4F6] mb-3">7. No warranty</h2>
            <p>The Service is provided "as is". We don't guarantee it will always be available, error-free, or fit any particular purpose. To the maximum extent allowed by law, we exclude all implied warranties.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#F3F4F6] mb-3">8. Liability</h2>
            <p>Our aggregate liability to you is limited to whichever is greater: (a) the total amount you paid to NoteLosh in the 12 months before the claim, or (b) $50. We are not liable for indirect, incidental, or consequential damages.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#F3F4F6] mb-3">9. Contact</h2>
            <p>Questions about these terms? Email <a href="mailto:support@notelosh.com" className="text-[#3B82F6] hover:underline">support@notelosh.com</a>.</p>
          </section>
        </div>

        <div className="mt-14 pt-8 border-t border-[#27272A] flex items-center justify-between text-sm text-[#6B7280]">
          <Link to="/privacy" className="hover:text-[#F3F4F6] transition-colors">← Privacy Policy</Link>
          <Link to="/" className="hover:text-[#F3F4F6] transition-colors flex items-center gap-1.5">
            <MessageSquareText className="h-3.5 w-3.5" /> notelosh.com
          </Link>
        </div>
      </main>
    </div>
  );
}
